import React from "react";
import { Box } from "@mui/material";
import { toast } from "react-toastify";
import GenericFormDialog from "../GenericFormDialog";
import { COLORS } from "../../assets/theme";
import {
  createClass,
  isScheduleConflictError,
  readScheduleConflicts,
  updateClass,
} from "../../services/ClassesService";
import { getErrorMessage } from "../../utils/apiResponse";
import {
  ClassDefinition,
  ScheduleConflict,
  UpdateClassPayload,
} from "./types";
import {
  buildClassFormFields,
  buildClassFormInitialValues,
  buildCreateClassPayloadFromValues,
  buildUpdateClassPayloadFromValues,
  readClassFormControlState,
} from "./classFormFields";
import { Option } from "./useClassOptions";
import ScheduleConflictsDialog from "./ScheduleConflictsDialog";
import DeactivateClassDialog from "./DeactivateClassDialog";

interface ClassFormDialogProps {
  open: boolean;
  classItem: ClassDefinition | null;
  unitOptions: Option[];
  coachOptions: Option[];
  categoryOptions: Option[];
  defaultUnitId?: string;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * A recurring class stores a rule, not sessions — create writes no session
 * rows at all and answers with the dates it projects instead. Update only
 * touches the definition (name, price, capacity), so the edit form hides the
 * schedule; changing when a class runs is its own operation, see
 * RescheduleClassDialog.
 */
const ClassFormDialog: React.FC<ClassFormDialogProps> = ({
  open,
  classItem,
  unitOptions,
  coachOptions,
  categoryOptions,
  defaultUnitId,
  onClose,
  onSaved,
}) => {
  const [submitting, setSubmitting] = React.useState(false);
  const [frequency, setFrequency] = React.useState("NONE");
  const [endMode, setEndMode] = React.useState("COUNT");
  const [conflicts, setConflicts] = React.useState<ScheduleConflict[]>([]);
  const [conflictMessage, setConflictMessage] = React.useState("");
  const [conflictsOpen, setConflictsOpen] = React.useState(false);
  // The edit payload held back while the deactivation warning is up, so the
  // whole save goes through as one PATCH once it's confirmed.
  const [pendingDeactivation, setPendingDeactivation] =
    React.useState<UpdateClassPayload | null>(null);
  const [statusValue, setStatusValue] = React.useState("Active");
  const [deactivated, setDeactivated] = React.useState(false);
  const isEdit = Boolean(classItem);
  const wasActive = classItem?.active !== false;

  React.useEffect(() => {
    if (open) {
      setFrequency("NONE");
      setEndMode("COUNT");
      setPendingDeactivation(null);
      setDeactivated(false);
      setStatusValue(wasActive ? "Active" : "Inactive");
    }
  }, [open, wasActive]);

  const fields = React.useMemo(
    () =>
      buildClassFormFields({
        isEdit,
        unitOptions,
        coachOptions,
        categoryOptions,
        frequency,
        endMode,
      }),
    [categoryOptions, coachOptions, endMode, frequency, isEdit, unitOptions]
  );

  const initialValues = React.useMemo(
    () => buildClassFormInitialValues({ classItem, defaultUnitId }),
    [classItem, defaultUnitId]
  );

  const handleSubmit = async (values: Record<string, any>) => {
    setSubmitting(true);
    try {
      if (classItem) {
        const payload = buildUpdateClassPayloadFromValues(values);
        if (!payload) return;

        // Switching a live class off is no longer a toggle — it cancels every
        // future session and refunds real money — so it goes through a
        // confirmation carrying the count instead of saving straight away.
        if (wasActive && payload.active === false) {
          setPendingDeactivation(payload);
          return;
        }

        const result = await updateClass(String(classItem.id), payload);

        // Reactivating frees the sessions nobody had booked; the ones people
        // were refunded for stay cancelled, so the number can look low.
        const restored = result.restored_sessions;
        toast.success(
          !wasActive && payload.active === true && typeof restored === "number"
            ? `Class reactivated — ${restored} session${
                restored === 1 ? "" : "s"
              } restored.`
            : "Class updated successfully!"
        );
      } else {
        const payload = buildCreateClassPayloadFromValues(values);
        if (!payload) return;

        const result = await createClass(payload);
        // A recurring class writes nothing up front, so `sessions` is empty and
        // the projected dates are the only thing there is to report on.
        const projected = result.preview_sessions.length;
        const openEnded =
          Boolean(payload.recurrence_frequency) &&
          !payload.recurrence_end &&
          !payload.recurrence_count;

        toast.success(
          !payload.recurrence_frequency
            ? "Class created successfully!"
            : openEnded
            ? `Class created — it runs until further notice${
                projected ? `, next ${projected} dates scheduled` : ""
              }.`
            : `Class created — ${projected} session${
                projected === 1 ? "" : "s"
              } scheduled.`
        );
      }
      onSaved();
      onClose();
    } catch (error) {
      // 409 comes back itemised now: which dates clash and why, rather than
      // just "the schedule is taken".
      // The 409 code is the signal; the itemised list may be empty, and the
      // dialog says so rather than leaving a bare toast to explain a rejection.
      const itemised = readScheduleConflicts(error);
      if (isScheduleConflictError(error)) {
        setConflicts(itemised);
        setConflictMessage(getErrorMessage(error, ""));
        setConflictsOpen(true);
        return;
      }
      toast.error(getErrorMessage(error, "Failed to save the class."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <GenericFormDialog
        open={open}
        onClose={onClose}
        title={isEdit ? "Edit Class" : "Add Class"}
        submitLabel={isEdit ? "Save" : "Create"}
        fields={fields}
        initialValues={initialValues}
        isSubmitting={submitting}
        onValuesChange={(values) => {
          const { frequency: nextFrequency, endMode: nextEndMode } =
            readClassFormControlState(values);
          setFrequency((prev) => (prev === nextFrequency ? prev : nextFrequency));
          setEndMode((prev) => (prev === nextEndMode ? prev : nextEndMode));
          const nextStatus = String(values.class_active || "Active");
          setStatusValue((prev) => (prev === nextStatus ? prev : nextStatus));
        }}
        onSubmit={handleSubmit}
        additionalActions={
          isEdit
            ? [
                <Box
                  key="hint"
                  sx={{
                    mr: "auto",
                    fontSize: 12,
                    fontWeight: wasActive && statusValue === "Inactive" ? 600 : 400,
                    color:
                      wasActive && statusValue === "Inactive"
                        ? "#d32f2f"
                        : COLORS.typographyColor,
                  }}
                >
                  {wasActive && statusValue === "Inactive"
                    ? "Setting this to Inactive cancels every upcoming session and refunds anyone booked."
                    : "This changes the class only — use Reschedule to move when it runs."}
                </Box>,
              ]
            : []
        }
      />

      <DeactivateClassDialog
        open={Boolean(pendingDeactivation)}
        classItem={classItem}
        payload={pendingDeactivation}
        onClose={() => {
          setPendingDeactivation(null);
          // Backing out of the warning returns to the form with the edits
          // still in it; only an actual deactivation closes the whole thing.
          if (!deactivated) return;
          setDeactivated(false);
          onClose();
        }}
        onDeactivated={() => {
          setDeactivated(true);
          onSaved();
        }}
      />

      <ScheduleConflictsDialog
        open={conflictsOpen}
        message={conflictMessage}
        conflicts={conflicts}
        onClose={() => setConflictsOpen(false)}
      />
    </>
  );
};

export default ClassFormDialog;
